import { useState, useEffect, useCallback } from 'react';
import {
  Clock,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  AlertTriangle,
  Send,
  HelpCircle,
  FileCheck,
  Award,
  LayoutGrid,
  Slash,
  X,
  Flag,
  AlertCircle
} from 'lucide-react';
import type {
  Question,
  QuestionClientView,
  QuestionOptionKey,
  ExamSessionMode,
  SubmitAnswerResponse
} from '@shared/types';

interface QuizEngineViewProps {
  sessionId?: string;
  mode: ExamSessionMode;
  questions: (Question | QuestionClientView)[];
  onFinishExam: () => void;
  onExit: () => void;
  onSubmitAnswerApi: (questionId: string, option: QuestionOptionKey, timeSpent: number) => Promise<SubmitAnswerResponse>;
  onToggleBookmarkApi: (questionId: string) => Promise<boolean>;
}

export function QuizEngineView({
  mode,
  questions,
  onFinishExam,
  onExit,
  onSubmitAnswerApi,
  onToggleBookmarkApi
}: QuizEngineViewProps) {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, QuestionOptionKey>>({});
  const [answerResults, setAnswerResults] = useState<Record<string, SubmitAnswerResponse>>({});
  const [bookmarkedIds, setBookmarkedIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'correct' | 'distractors'>('correct');
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());

  // Prometric In-Exam Features: Option Elimination & Question Navigation Grid
  const [eliminatedOptions, setEliminatedOptions] = useState<Record<string, Set<QuestionOptionKey>>>({});
  const [isGridModalOpen, setIsGridModalOpen] = useState<boolean>(false);

  // Prometric Pacing: 1.56 minutes (~93.8s) per question. e.g., 165 Qs = 258 mins = 15,480s
  const totalAllocatedSeconds = Math.max(600, Math.round(questions.length * 93.818));
  const remainingSeconds = mode === 'timed_exam' ? Math.max(0, totalAllocatedSeconds - elapsedSeconds) : elapsedSeconds;

  const currentQ = questions[currentIndex];
  const qId = currentQ?.id;
  const userOption = qId ? selectedAnswers[qId] : undefined;
  const result = qId ? answerResults[qId] : undefined;
  const isAnswered = !!userOption;

  const currentEliminated = (qId && eliminatedOptions[qId]) || new Set<QuestionOptionKey>();

  // Timer logic: countdown for timed_exam, stopwatch for study
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedSeconds(prev => {
        const next = prev + 1;
        if (mode === 'timed_exam' && next >= totalAllocatedSeconds) {
          clearInterval(interval);
          onFinishExam();
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [mode, totalAllocatedSeconds, onFinishExam]);

  // Track per-question time
  useEffect(() => {
    setQuestionStartTime(Date.now());
  }, [currentIndex]);

  const handleSelectOption = useCallback(async (optionKey: QuestionOptionKey) => {
    if (!currentQ || isSubmitting) return;
    // In study mode, once answered, keep feedback visible
    if (mode === 'study' && isAnswered) return;

    // If option was eliminated, restore it when clicked
    if (currentQ.id && eliminatedOptions[currentQ.id]?.has(optionKey)) {
      setEliminatedOptions(prev => {
        const nextSet = new Set(prev[currentQ.id]);
        nextSet.delete(optionKey);
        return { ...prev, [currentQ.id]: nextSet };
      });
    }

    const timeSpent = Math.max(1, Math.round((Date.now() - questionStartTime) / 1000));
    setSelectedAnswers(prev => ({ ...prev, [currentQ.id]: optionKey }));

    try {
      setIsSubmitting(true);
      const res = await onSubmitAnswerApi(currentQ.id, optionKey, timeSpent);
      setAnswerResults(prev => ({ ...prev, [currentQ.id]: res }));
    } catch (err) {
      console.error('Failed to submit answer:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [currentQ, isSubmitting, mode, isAnswered, questionStartTime, onSubmitAnswerApi, eliminatedOptions]);

  // Toggle distractor elimination (Prometric strike-through)
  const handleToggleEliminate = (e: React.MouseEvent, optionKey: QuestionOptionKey) => {
    e.stopPropagation();
    if (!qId || (mode === 'study' && isAnswered)) return;

    setEliminatedOptions(prev => {
      const currentSet = new Set(prev[qId] || []);
      if (currentSet.has(optionKey)) {
        currentSet.delete(optionKey);
      } else {
        currentSet.add(optionKey);
        // If it was selected, deselect it
        if (selectedAnswers[qId] === optionKey) {
          setSelectedAnswers(ans => {
            const nextAns = { ...ans };
            delete nextAns[qId];
            return nextAns;
          });
        }
      }
      return { ...prev, [qId]: currentSet };
    });
  };

  // Keyboard Hotkey Support: A/B/C/D and 1/2/3/4, F to flag, G to open grid
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)) return;

      const key = e.key.toUpperCase();
      if (key === 'A' || key === '1') handleSelectOption('A');
      else if (key === 'B' || key === '2') handleSelectOption('B');
      else if (key === 'C' || key === '3') handleSelectOption('C');
      else if (key === 'D' || key === '4') handleSelectOption('D');
      else if (key === 'F') handleToggleBookmark();
      else if (key === 'G') setIsGridModalOpen(prev => !prev);
      else if (e.key === 'ArrowRight' && currentIndex < questions.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
        setCurrentIndex(prev => prev - 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSelectOption, currentIndex, questions.length]);

  const handleToggleBookmark = async () => {
    if (!currentQ) return;
    try {
      const isBookmarked = await onToggleBookmarkApi(currentQ.id);
      setBookmarkedIds(prev => {
        const next = new Set(prev);
        if (isBookmarked) next.add(currentQ.id);
        else next.delete(currentQ.id);
        return next;
      });
    } catch (err) {
      console.error('Bookmark toggle error:', err);
    }
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const isCurrentBookmarked = qId ? bookmarkedIds.has(qId) : false;
  const progressPercentage = ((currentIndex + 1) / questions.length) * 100;
  const answeredCount = Object.keys(selectedAnswers).length;
  const flaggedCount = bookmarkedIds.size;

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fadeIn pb-16">
      {/* Prometric Urgent Time Warning Banner */}
      {mode === 'timed_exam' && remainingSeconds <= 300 && remainingSeconds > 0 && (
        <div className="bg-rose-500/15 border border-rose-500/40 text-rose-800 dark:text-rose-200 px-4 py-3 rounded-2xl flex items-center justify-between text-xs font-semibold animate-pulse shadow-sm">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-4 h-4 text-rose-600 dark:text-rose-400 flex-shrink-0" />
            <span>Prometric Time Warning: Less than 5 minutes remaining! Finalize answers and review flagged items.</span>
          </div>
          <span className="font-mono font-bold bg-rose-500/20 px-2 py-0.5 rounded">
            {formatTime(remainingSeconds)}
          </span>
        </div>
      )}

      {/* Top Header Bar */}
      <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur border border-slate-200 dark:border-slate-800 rounded-2xl p-4 sm:p-5 shadow-sm dark:shadow-xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 sticky top-20 z-40 transition-colors duration-200">
        <div className="flex items-center space-x-3">
          <button
            onClick={onExit}
            className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 transition border border-slate-200 dark:border-slate-700/60 shadow-xs"
            title="Exit Session"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center space-x-2">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full border ${
                mode === 'timed_exam'
                  ? 'bg-rose-500/10 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300 border-rose-500/30'
                  : 'bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30'
              }`}>
                {mode === 'timed_exam' ? 'Prometric Timed' : 'Study Mode'}
              </span>
              <span className="text-xs font-mono text-slate-500 dark:text-slate-400">
                Q {currentIndex + 1} of {questions.length}
              </span>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-2">
              <span>{answeredCount} of {questions.length} Answered</span>
              {flaggedCount > 0 && (
                <span className="text-amber-600 dark:text-amber-400 font-medium">• {flaggedCount} Flagged</span>
              )}
            </div>
          </div>
        </div>

        {/* Center Progress Bar */}
        <div className="flex-1 max-w-xs hidden md:block">
          <div className="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-2 overflow-hidden border border-slate-200 dark:border-slate-700/60">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                mode === 'timed_exam' ? 'bg-indigo-600 dark:bg-indigo-500' : 'bg-emerald-600 dark:bg-emerald-500'
              }`}
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>

        {/* Right Controls: Question Grid, Timer, Flag, Finish */}
        <div className="flex items-center space-x-2.5 self-end sm:self-auto">
          {/* Question Review Grid Button */}
          <button
            onClick={() => setIsGridModalOpen(true)}
            className="px-3 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 text-xs font-mono flex items-center space-x-1.5 transition shadow-xs"
            title="Open Question Review Grid (Hot-key: G)"
          >
            <LayoutGrid className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            <span className="hidden sm:inline">Review Grid</span>
          </button>

          {/* Timer Display */}
          <div className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl border text-xs font-mono shadow-xs ${
            mode === 'timed_exam' && remainingSeconds <= 300
              ? 'bg-rose-500/15 border-rose-500/40 text-rose-700 dark:text-rose-300 font-bold animate-pulse'
              : 'bg-slate-100/90 dark:bg-slate-950/80 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-200'
          }`}>
            <Clock className={`w-4 h-4 ${mode === 'timed_exam' && remainingSeconds <= 300 ? 'text-rose-600' : 'text-indigo-600 dark:text-indigo-400'}`} />
            <span>{formatTime(remainingSeconds)}</span>
          </div>

          {/* Flag / Bookmark Button */}
          <button
            onClick={handleToggleBookmark}
            className={`p-2 rounded-xl border transition ${
              isCurrentBookmarked
                ? 'bg-amber-500/15 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/40 shadow-xs'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700/60 hover:text-slate-800 dark:hover:text-white'
            }`}
            title="Flag Question for Review (Hot-key: F)"
          >
            <Flag className="w-4 h-4" />
          </button>

          <button
            onClick={onFinishExam}
            className="px-4 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-xs transition shadow-md shadow-indigo-600/20 dark:shadow-indigo-950 flex items-center space-x-1.5"
          >
            <span>Finish</span>
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Main Question Card */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 shadow-sm dark:shadow-2xl space-y-6 transition-colors duration-200">
        {/* Metadata & Regulation Badges */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 pb-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-md border ${
              currentQ?.bloom_level === 'Analysis'
                ? 'bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800/60'
                : currentQ?.bloom_level === 'Application'
                ? 'bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/60'
                : 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/60'
            }`}>
              Bloom: {currentQ?.bloom_level || 'Application'}
            </span>

            {currentQ?.primary_citation && (
              <span className="text-xs font-mono bg-slate-100 dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-900/60 px-2.5 py-1 rounded-md flex items-center gap-1.5">
                <FileCheck className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                <span>{currentQ.primary_citation}</span>
              </span>
            )}

            {currentQ?.domain_name && (
              <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                • {currentQ.domain_code}: {currentQ.domain_name}
              </span>
            )}
          </div>

          {isCurrentBookmarked && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 dark:text-amber-400 bg-amber-500/15 border border-amber-500/30 px-2.5 py-1 rounded-lg">
              <Flag className="w-3.5 h-3.5" />
              <span>FLAGGED FOR REVIEW</span>
            </span>
          )}
        </div>

        {/* Scenario Stem Narrative */}
        <div className="prose dark:prose-invert max-w-none">
          <p className="text-base sm:text-lg text-slate-900 dark:text-slate-100 font-normal leading-relaxed">
            {currentQ?.scenario_stem}
          </p>
        </div>

        {/* Answer Options with Prometric Strikethrough & Hotkey letter tiles */}
        <div className="space-y-3 pt-2">
          {(['A', 'B', 'C', 'D'] as const).map(optKey => {
            const optText = (currentQ as any)?.[`option_${optKey.toLowerCase()}`] as string;
            const isSelected = userOption === optKey;
            const isEliminated = currentEliminated.has(optKey);
            const isCorrect = result?.correct_option === optKey;
            const hasFeedback = mode === 'study' && isAnswered && result;

            let cardStyles = 'bg-slate-50/70 dark:bg-slate-950/50 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200 hover:border-slate-300 dark:hover:border-slate-700 hover:bg-slate-100/80 dark:hover:bg-slate-950/80 shadow-xs';
            let badgeStyles = 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-700';

            if (isEliminated) {
              cardStyles = 'bg-slate-100/40 dark:bg-slate-950/20 border-slate-200/50 dark:border-slate-800/50 text-slate-400 line-through opacity-50';
              badgeStyles = 'bg-slate-200 dark:bg-slate-800 text-slate-400 border-slate-300 dark:border-slate-700 line-through';
            } else if (hasFeedback) {
              if (isCorrect) {
                cardStyles = 'bg-emerald-50/80 dark:bg-emerald-950/40 border-emerald-500 text-emerald-950 dark:text-emerald-100 shadow-sm dark:shadow-emerald-950/40';
                badgeStyles = 'bg-emerald-600 dark:bg-emerald-500 text-white dark:text-slate-950 border-emerald-500 dark:border-emerald-400 font-bold';
              } else if (isSelected && !isCorrect) {
                cardStyles = 'bg-rose-50/80 dark:bg-rose-950/40 border-rose-500 text-rose-950 dark:text-rose-100 shadow-sm dark:shadow-rose-950/40';
                badgeStyles = 'bg-rose-600 dark:bg-rose-500 text-white border-rose-500 dark:border-rose-400 font-bold';
              } else {
                cardStyles = 'bg-slate-50/40 dark:bg-slate-950/30 border-slate-200/80 dark:border-slate-800 text-slate-400 opacity-60';
              }
            } else if (isSelected) {
              cardStyles = 'bg-indigo-50/80 dark:bg-indigo-950/50 border-indigo-500 text-indigo-950 dark:text-indigo-100 ring-1 ring-indigo-500/50 shadow-sm';
              badgeStyles = 'bg-indigo-600 text-white border-indigo-500 dark:border-indigo-400 font-bold';
            }

            return (
              <div
                key={optKey}
                onClick={() => handleSelectOption(optKey)}
                className={`w-full text-left p-4 sm:p-5 rounded-2xl border transition flex items-start space-x-4 cursor-pointer relative group ${cardStyles}`}
              >
                {/* Hotkey Letter Tile */}
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-mono font-bold flex-shrink-0 mt-0.5 border shadow-xs ${badgeStyles}`}>
                  {optKey}
                </div>

                <div className={`flex-1 text-sm sm:text-base leading-relaxed pt-0.5 ${isEliminated ? 'line-through' : ''}`}>
                  {optText}
                </div>

                {/* Right controls: Feedback Icon OR Prometric Strike-Through Toggle */}
                <div className="flex items-center space-x-2 flex-shrink-0">
                  {hasFeedback && isCorrect && (
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-1" />
                  )}
                  {hasFeedback && isSelected && !isCorrect && (
                    <XCircle className="w-5 h-5 text-rose-600 dark:text-rose-400 mt-1" />
                  )}

                  {/* Strike-Through Distractor Elimination Button */}
                  {!hasFeedback && (
                    <button
                      onClick={(e) => handleToggleEliminate(e, optKey)}
                      className={`p-1.5 rounded-lg border text-xs transition ${
                        isEliminated
                          ? 'bg-rose-500/20 text-rose-700 dark:text-rose-300 border-rose-500/40'
                          : 'bg-transparent text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 border-transparent hover:border-slate-200 dark:hover:border-slate-700'
                      }`}
                      title={isEliminated ? 'Restore this option' : 'Strike through / eliminate distractor'}
                    >
                      <Slash className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Hotkey hint strip */}
        <div className="text-[11px] text-slate-500 dark:text-slate-400 font-mono flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 dark:border-slate-800/80 pt-3">
          <span>Shortcuts: <kbd className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-semibold">A-D</kbd> select • <kbd className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-semibold">F</kbd> flag • <kbd className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-semibold">G</kbd> grid</span>
          <span><kbd className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-semibold">←</kbd> <kbd className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-semibold">→</kbd> navigate</span>
        </div>

        {/* Study Mode Feedback Reveal */}
        {mode === 'study' && isAnswered && result && (
          <div className="mt-6 rounded-2xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 overflow-hidden animate-slideDown shadow-md dark:shadow-xl">
            {/* Verdict Banner */}
            <div className={`p-4 flex items-center justify-between border-b ${
              result.is_correct
                ? 'bg-emerald-50 dark:bg-emerald-950/50 border-emerald-200 dark:border-emerald-900/60 text-emerald-900 dark:text-emerald-300'
                : 'bg-rose-50 dark:bg-rose-950/50 border-rose-200 dark:border-rose-900/60 text-rose-900 dark:text-rose-300'
            }`}>
              <div className="flex items-center space-x-2.5">
                {result.is_correct ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                )}
                <span className="font-bold text-sm sm:text-base">
                  {result.is_correct
                    ? 'Compliant Solution — Correct Option ' + result.correct_option
                    : 'Regulatory Deviation — Option ' + result.correct_option + ' is Required'}
                </span>
              </div>
              <span className="text-xs font-mono font-medium px-2 py-0.5 rounded bg-white/80 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 shadow-xs">
                Official Rationale
              </span>
            </div>

            {/* Explanation Navigation Tabs */}
            <div className="flex border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
              <button
                onClick={() => setActiveTab('correct')}
                className={`px-5 py-3 text-xs font-bold transition flex items-center space-x-2 border-b-2 ${
                  activeTab === 'correct'
                    ? 'border-indigo-600 dark:border-indigo-500 text-indigo-700 dark:text-indigo-300 bg-white dark:bg-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <span>Why Answer {result.correct_option} Is Correct</span>
              </button>

              <button
                onClick={() => setActiveTab('distractors')}
                className={`px-5 py-3 text-xs font-bold transition flex items-center space-x-2 border-b-2 ${
                  activeTab === 'distractors'
                    ? 'border-indigo-600 dark:border-indigo-500 text-indigo-700 dark:text-indigo-300 bg-white dark:bg-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                <HelpCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <span>Why Other Options Violate Regulation</span>
              </button>
            </div>

            {/* Tab Contents */}
            <div className="p-5 sm:p-6 space-y-4">
              {activeTab === 'correct' ? (
                <div className="p-4 rounded-xl bg-emerald-50/80 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 text-emerald-950 dark:text-emerald-200 text-sm leading-relaxed">
                  <div className="font-semibold text-emerald-800 dark:text-emerald-300 mb-1 flex items-center gap-1.5">
                    <Award className="w-4 h-4" /> Compliant Justification:
                  </div>
                  {result.rationales[result.correct_option]}
                </div>
              ) : (
                <div className="space-y-3">
                  {(['A', 'B', 'C', 'D'] as const)
                    .filter(k => k !== result.correct_option)
                    .map(k => (
                      <div
                        key={k}
                        className={`p-3.5 rounded-xl border text-xs sm:text-sm leading-relaxed ${
                          userOption === k
                            ? 'bg-rose-50/80 dark:bg-rose-950/30 border-rose-200 dark:border-rose-900/60 text-rose-950 dark:text-rose-200'
                            : 'bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300'
                        }`}
                      >
                        <strong className="text-slate-900 dark:text-slate-200 mr-2 font-mono">Option {k}:</strong>
                        {result.rationales[k]}
                      </div>
                    ))}
                </div>
              )}

              {/* Expandable Direct Regulatory Excerpt Drawer */}
              <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden mt-4">
                <button
                  onClick={() => setDrawerOpen(!drawerOpen)}
                  className="w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 dark:bg-slate-900/80 dark:hover:bg-slate-900 text-left text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center justify-between transition"
                >
                  <div className="flex items-center space-x-2">
                    <BookOpen className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <span>Direct Regulatory Excerpt & Statutory Citation</span>
                  </div>
                  <span className="text-xs text-indigo-600 dark:text-indigo-400 font-mono">
                    {drawerOpen ? 'Hide Excerpt ▲' : 'View Excerpt ▼'}
                  </span>
                </button>

                {drawerOpen && (
                  <div className="p-4 bg-slate-50/80 dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 text-xs font-mono space-y-2.5 text-slate-700 dark:text-slate-300 animate-fadeIn">
                    <div className="flex items-start gap-2">
                      <span className="text-indigo-600 dark:text-indigo-400 font-bold">Primary Citation:</span>
                      <span className="text-slate-900 dark:text-slate-200">{result.citations.primary}</span>
                    </div>
                    {result.citations.secondary && (
                      <div className="flex items-start gap-2">
                        <span className="text-slate-500 dark:text-slate-400 font-bold">Secondary Citation:</span>
                        <span className="text-slate-800 dark:text-slate-300">{result.citations.secondary}</span>
                      </div>
                    )}
                    <p className="text-slate-500 dark:text-slate-400 text-[11px] pt-1 border-t border-slate-200 dark:border-slate-900">
                      Grounded in Title 21 Code of Federal Regulations and EU GMP Volume 4.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bottom Pagination Controls */}
        <div className="flex items-center justify-between pt-6 border-t border-slate-100 dark:border-slate-800">
          <button
            onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
            disabled={currentIndex === 0}
            className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 disabled:opacity-30 disabled:hover:bg-slate-100 dark:disabled:hover:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold transition border border-slate-200 dark:border-slate-700 flex items-center space-x-1.5 shadow-xs"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Previous</span>
          </button>

          <div className="text-xs text-slate-500 dark:text-slate-400 font-mono hidden sm:block">
            Question {currentIndex + 1} / {questions.length}
          </div>

          {currentIndex < questions.length - 1 ? (
            <button
              onClick={() => setCurrentIndex(prev => prev + 1)}
              className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold transition shadow-md shadow-indigo-600/20 dark:shadow-indigo-950 flex items-center space-x-1.5"
            >
              <span>Next Question</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={onFinishExam}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-bold transition shadow-md shadow-emerald-600/20 dark:shadow-emerald-950 flex items-center space-x-2"
            >
              <span>Finish & View Score</span>
              <CheckCircle2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Prometric Question Review Grid Modal */}
      {isGridModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="p-5 sm:p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <LayoutGrid className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  <span>Prometric Question Navigation Grid</span>
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Click any question number to jump directly
                </p>
              </div>
              <button
                onClick={() => setIsGridModalOpen(false)}
                className="p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Legend */}
            <div className="px-6 py-3 bg-slate-50 dark:bg-slate-950/50 border-b border-slate-100 dark:border-slate-800 flex flex-wrap items-center gap-4 text-xs font-mono">
              <div className="flex items-center gap-1.5">
                <div className="w-3.5 h-3.5 rounded bg-indigo-600"></div>
                <span className="text-slate-700 dark:text-slate-300">Answered ({answeredCount})</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3.5 h-3.5 rounded bg-amber-500"></div>
                <span className="text-slate-700 dark:text-slate-300">Flagged ({flaggedCount})</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3.5 h-3.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800"></div>
                <span className="text-slate-500">Unanswered ({questions.length - answeredCount})</span>
              </div>
            </div>

            {/* Tiles Grid */}
            <div className="p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2.5">
                {questions.map((q, idx) => {
                  const isCurrent = idx === currentIndex;
                  const isQAnswered = !!selectedAnswers[q.id];
                  const isQFlagged = bookmarkedIds.has(q.id);

                  let tileStyle = 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-indigo-400';

                  if (isQFlagged && isQAnswered) {
                    tileStyle = 'bg-amber-500 text-white border-amber-600 font-bold shadow-xs';
                  } else if (isQFlagged) {
                    tileStyle = 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/50 font-bold';
                  } else if (isQAnswered) {
                    tileStyle = 'bg-indigo-600 text-white border-indigo-600 font-bold shadow-xs';
                  }

                  return (
                    <button
                      key={q.id}
                      onClick={() => {
                        setCurrentIndex(idx);
                        setIsGridModalOpen(false);
                      }}
                      className={`h-10 rounded-xl border flex items-center justify-center text-xs font-mono transition relative ${tileStyle} ${
                        isCurrent ? 'ring-2 ring-indigo-500 ring-offset-2 dark:ring-offset-slate-900 font-black scale-105' : ''
                      }`}
                    >
                      <span>{idx + 1}</span>
                      {isQFlagged && (
                        <div className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-amber-300"></div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-100 dark:border-slate-800 flex justify-end">
              <button
                onClick={() => setIsGridModalOpen(false)}
                className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-xs font-semibold transition"
              >
                Close Review Grid
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
